import React, { useState, useEffect, useCallback } from 'react';
import {
    X, ClipboardList, MessageSquare, BarChart3,
    RefreshCw, TrendingUp, Download
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    BarChart,
    DataTable,
    LineChart,
    DonutChart,
    AIInsightCard,
    ExpandableVisualization
} from '../Analytics';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';

const SurveysAnalyticsDrawer = ({ isOpen, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps } = useDrawerResize({
        minWidth: 400, maxWidth: 800, defaultWidth: 600,
        storageKey: 'surveysAnalyticsDrawerWidth',
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/analytics/surveys', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(response.data.analytics);
        } catch (error) {
            console.error('Error fetching survey analytics:', error);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'surveys', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'survey-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Survey analytics exported');
        } catch { toast.error('Export failed'); }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
            document.body.style.overflow = 'hidden';
        } else {
            setData(null);
            document.body.style.overflow = 'unset';
            setLoading(true);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, fetchData]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col animate-slide-in-right"
                style={{ width: `${drawerWidth}px`, maxWidth: '100vw', transition: isDragging ? 'none' : undefined }}
            >
                {/* ── Drag handle (left edge) ── */}
                <div {...dragHandleProps} />
                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                            <ClipboardList className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Survey Analytics</h2>
                            <p className="text-sm text-gray-500">Response rates and feedback insights</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 space-y-4">
                            <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                            <p className="text-gray-500 font-medium">Loading analytics...</p>
                        </div>
                    ) : data ? (
                        <div className="space-y-6">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <StatCard
                                    title="Active Surveys"
                                    value={data.activeSurveys || 0}
                                    icon={ClipboardList}
                                    color="blue"
                                />
                                <StatCard
                                    title="Total Responses"
                                    value={data.totalResponses || 0}
                                    icon={MessageSquare}
                                    color="indigo"
                                />
                                <StatCard
                                    title="Avg Response Rate"
                                    value={`${data.avgResponseRate || 0}%`}
                                    icon={TrendingUp}
                                    color={data.avgResponseRate >= 50 ? 'green' : 'amber'}
                                />
                                <StatCard
                                    title="Draft Surveys"
                                    value={data.draftSurveys || 0}
                                    icon={BarChart3}
                                    color="gray"
                                />
                            </div>

                            {/* Response Trend Chart */}
                            {data.responsesHistory && (
                                <ExpandableVisualization
                                    title="Responses (Last 30 Days)"
                                    data={data.responsesHistory}
                                    columns={[{ key: 'date', label: 'Date' }, { key: 'count', label: 'Responses' }]}
                                    exportFilename="survey-responses-trend"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] p-6 shadow-sm">
                                        <h3 className="font-semibold text-gray-800 mb-4">Responses (Last 30 Days)</h3>
                                        <BarChart
                                            data={data.responsesHistory.map(d => ({
                                                label: d.date,
                                                value: d.count,
                                                color: '#6366f1'
                                            }))}
                                            showValues
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Survey Completions Trend */}
                            {data.completionsTrend && data.completionsTrend.length > 0 && (
                                <ExpandableVisualization
                                    title="Survey Completions Over Time"
                                    data={data.completionsTrend.map(d => ({ date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), completions: d.completions }))}
                                    columns={[{ key: 'date', label: 'Date' }, { key: 'completions', label: 'Completions' }]}
                                    exportFilename="survey-completions-trend"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Survey Completions Over Time</h3>
                                        <LineChart
                                            data={data.completionsTrend.map(d => ({
                                                date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                value: d.completions
                                            }))}
                                            height={200}
                                            lineColor="#8b5cf6"
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Top Performing Surveys Table */}
                            {data.topSurveys && data.topSurveys.length > 0 && (
                                <ExpandableVisualization
                                    title="High Engagement Surveys"
                                    data={data.topSurveys}
                                    columns={[{ key: 'title', label: 'Survey' }, { key: 'responses', label: 'Responses' }, { key: 'responseRate', label: 'Response Rate %' }, { key: 'status', label: 'Status' }]}
                                    exportFilename="top-surveys"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] overflow-hidden shadow-sm">
                                        <div className="p-4 border-b border-[#E5E1DD]">
                                            <h3 className="font-semibold text-gray-800">High Engagement Surveys</h3>
                                        </div>
                                        <DataTable
                                            data={data.topSurveys}
                                            columns={[
                                                { key: 'title', label: 'Survey Title', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                                { key: 'responses', label: 'Responses' },
                                                {
                                                    key: 'responseRate',
                                                    label: 'Response Rate',
                                                    render: (val) => (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full ${val >= 50 ? 'bg-green-500' : val >= 30 ? 'bg-amber-500' : 'bg-red-500'}`}
                                                                    style={{ width: `${val}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-xs">{val}%</span>
                                                        </div>
                                                    )
                                                },
                                                { key: 'status', label: 'Status', render: (val) => <span className="capitalize text-gray-600">{val}</span> }
                                            ]}
                                            searchable
                                            searchKeys={['title']}
                                            pageSize={5}
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Sentiment Distribution */}
                            {data.sentimentDistribution && data.sentimentDistribution.length > 0 && (
                                <ExpandableVisualization
                                    title="Sentiment Distribution"
                                    data={data.sentimentDistribution}
                                    columns={[{ key: 'label', label: 'Sentiment' }, { key: 'count', label: 'Count' }]}
                                    exportFilename="sentiment-distribution"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <h3 className="font-semibold text-gray-800 mb-4">Sentiment Distribution</h3>
                                        <DonutChart
                                            data={data.sentimentDistribution.map(d => ({
                                                label: d.label.charAt(0).toUpperCase() + d.label.slice(1),
                                                value: d.count,
                                                color: d.label === 'positive' ? '#22c55e' : d.label === 'negative' ? '#ef4444' : d.label === 'neutral' ? '#f59e0b' : '#9ca3af'
                                            }))}
                                            size={140}
                                            totalLabel="Responses"
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Per-Question Analysis */}
                            {data.questionScores && data.questionScores.length > 0 && (
                                <ExpandableVisualization
                                    title="Per-Question Analysis"
                                    subtitle="Average scores by question"
                                    data={data.questionScores}
                                    columns={[{ key: 'questionText', label: 'Question' }, { key: 'avgScore', label: 'Avg Score' }, { key: 'totalResponses', label: 'Responses' }, { key: 'skipped', label: 'Skipped' }]}
                                    exportFilename="question-analysis"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] overflow-hidden shadow-sm">
                                        <div className="p-4 border-b border-[#E5E1DD]">
                                            <h3 className="font-semibold text-gray-800">Per-Question Analysis</h3>
                                            <p className="text-sm text-gray-500">Average scores by question</p>
                                        </div>
                                        <DataTable
                                            data={data.questionScores}
                                            columns={[
                                                { key: 'questionText', label: 'Question', render: (val) => <span className="font-medium text-gray-900 truncate max-w-xs block">{val}</span> },
                                                { key: 'avgScore', label: 'Avg Score', render: (val) => <span className="font-semibold">{val}</span> },
                                                { key: 'totalResponses', label: 'Responses' },
                                                { key: 'skipped', label: 'Skipped' },
                                            ]}
                                            pageSize={5}
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* AI Insights */}
                            <AIInsightCard
                                title="Survey Insights"
                                insight={`Average survey response rate is ${data.avgResponseRate || 0}%. ${data.topSurveys?.length > 0 ? `Top performing survey: "${data.topSurveys[0].title}".` : 'No significant survey data yet.'}`}
                                suggestions={[
                                    "Which survey has the lowest response rate?",
                                    "Show me trending survey topics",
                                    "How to improve survey engagement?",
                                    "Analysis of recent survey feedback"
                                ]}
                                context={{ surveys: data, type: 'surveys' }}
                            />

                            {/* AI Chat Panel */}
                            <AnalyticsAIPanel
                                analyticsContext={{
                                    type: 'surveys',
                                    title: 'Survey Analytics',
                                    data: data,
                                }}
                            />

                            {/* Notification Settings */}
                            <NotificationSettingsPanel
                                analyticsContext={{
                                    type: 'surveys',
                                    title: 'Survey Analytics',
                                    data: data,
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <div className="bg-gray-50 rounded-lg p-6 max-w-sm mx-auto border border-gray-200">
                                <BarChart3 className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-gray-900 font-medium mb-1">No Analytics Available</h3>
                                <p className="text-sm text-gray-500">
                                    We couldn't load survey analytics at this time.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SurveysAnalyticsDrawer;
